import { RefObject, useEffect } from 'react';

/**
 * Tabellen der Verwaltung werden auf dem Handy zu Karten (CSS in App.css, `.admin-table-scroll`
 * unter 640px). Damit eine Zelle dort sagt, was sie ist, braucht sie die Spaltenüberschrift als
 * `data-label`. Die setzt dieser Hook für alle Tabellen unter `wurzel` — einmal zentral statt in
 * 21 Seiten von Hand, und auch für Zeilen, die erst später gerendert werden (Nachladen, Filter).
 *
 * Regeln: Die Beschriftung kommt aus dem Text des `th` an derselben Spaltenposition (colSpan
 * zählt mit). Eine Zelle über mehrere Spalten (aufgeklappte Detailzeile) bekommt eine leere
 * Beschriftung und steht dann als Block. Ein von Hand gesetztes `data-label` bleibt.
 */
export function beschrifteTabellen(wurzel: ParentNode) {
  wurzel.querySelectorAll<HTMLTableElement>('.admin-table-scroll table').forEach(tabelle => {
    const koepfe: string[] = [];
    const kopfZeile = tabelle.tHead?.rows[tabelle.tHead.rows.length - 1];
    if (!kopfZeile) return;
    Array.from(kopfZeile.cells).forEach(th => {
      const text = (th.textContent || '').replace(/\s+/g, ' ').trim();
      for (let i = 0; i < (th.colSpan || 1); i++) koepfe.push(text);
    });
    Array.from(tabelle.tBodies).forEach(body => {
      Array.from(body.rows).forEach(zeile => {
        let spalte = 0;
        Array.from(zeile.cells).forEach(zelle => {
          const breite = zelle.colSpan || 1;
          if (!zelle.hasAttribute('data-label-hand')) {
            const label = breite > 1 ? '' : (koepfe[spalte] ?? '');
            if (zelle.getAttribute('data-label') !== label) zelle.setAttribute('data-label', label);
          }
          spalte += breite;
        });
      });
    });
  });
}

export function useTabellenKarten(wurzel: RefObject<HTMLElement>) {
  useEffect(() => {
    const el = wurzel.current;
    if (!el || typeof MutationObserver === 'undefined') return;
    let geplant = 0;
    const lauf = () => { geplant = 0; beschrifteTabellen(el); };
    lauf();
    // Gebündelt pro Frame: Listen rendern stückweise nach, das löst viele Mutationen aus.
    const beobachter = new MutationObserver(() => {
      if (!geplant) geplant = window.requestAnimationFrame(lauf);
    });
    beobachter.observe(el, { childList: true, subtree: true });
    return () => {
      beobachter.disconnect();
      if (geplant) window.cancelAnimationFrame(geplant);
    };
  }, [wurzel]);
}
